import moment from 'moment'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { parseFormattedDateLabel } from 'modules/finance/components/FinancialWorkspace/lib/datePeriod'
import { ClickHeader, HeaderData } from 'modules/finance/components/FinancialWorkspace/types/table'

export const useHeaders = (onClick?: ClickHeader): HeaderData[] => {
  const {
    hasComparisonDate,
    formattedState: { formattedStartDate, formattedEndDate, formattedCompareToStartDate },
  } = useFinancialWorkspace()

  return useUnsafeMemo(() => {
    const headers: HeaderData[] = []
    const start = moment(formattedStartDate)
    const end = moment(formattedEndDate)
    const compareToStartDate = moment(formattedCompareToStartDate)

    const monthDiff = end.diff(start, 'months') + 1

    Array.from({ length: monthDiff }, (_, i) => {
      const date = moment(start).add(i, 'months')

      if (hasComparisonDate) {
        // if period is just a month then show Month Year e.g June 2025. Otherwise only show the year e.g 2025
        // This is because when there is a comparison date, it means we are showing data for 2 periods.
        // and in the case where the period is more than just a month difference, we will be displaying a super header that has the month.
        const format = monthDiff <= 1 ? 'MMMM YYYY' : 'YYYY'
        const comparisonDate = moment(compareToStartDate).add(i, 'months')

        headers.push({
          value: comparisonDate,
          label: comparisonDate.format(format),
          onClick: onClick ? () => onClick(comparisonDate) : undefined,
          chartLabel: parseFormattedDateLabel(comparisonDate.format('MMM YYYY')),
        })

        headers.push({
          value: date,
          label: date.format(format),
          onClick: onClick ? () => onClick(date) : undefined,
          chartLabel: parseFormattedDateLabel(date.format('MMM YYYY')),
        })

        headers.push({
          value: moment(),
          label: i18n.t('finance.financial_workspace.breakdown_table.growth'),
        })
      } else {
        const format = monthDiff <= 1 ? 'MMMM YYYY' : 'MMMM'
        headers.push({
          value: date,
          label: date.format(format),
          onClick: onClick ? () => onClick(date) : undefined,
          chartLabel: parseFormattedDateLabel(date.format('MMM YYYY')),
        })
      }
    })

    return headers
  }, [
    onClick,
    formattedEndDate,
    hasComparisonDate,
    formattedStartDate,
    formattedCompareToStartDate,
  ])
}
